<template>
  <div class="base-converter">
    <h1>进制转换工具</h1>
    
    <div class="converter-container">
      <!-- 输入区域 -->
      <div class="input-section">
        <div class="number-input">
          <div class="input-group">
            <div class="label">二进制 (2)</div>
            <el-input
              v-model="binary"
              :class="{ 'active': activeBase === 2 }"
              @focus="setActiveBase(2)"
              @input="handleBinaryInput"
              placeholder="输入二进制数"
            >
              <template #prefix>0b</template>
            </el-input>
          </div>

          <div class="input-group">
            <div class="label">八进制 (8)</div>
            <el-input
              v-model="octal"
              :class="{ 'active': activeBase === 8 }"
              @focus="setActiveBase(8)"
              @input="handleOctalInput"
              placeholder="输入八进制数"
            >
              <template #prefix>0o</template>
            </el-input>
          </div>

          <div class="input-group">
            <div class="label">十进制 (10)</div>
            <el-input
              v-model="decimal"
              :class="{ 'active': activeBase === 10 }"
              @focus="setActiveBase(10)"
              @input="handleDecimalInput"
              placeholder="输入十进制数"
            />
          </div>

          <div class="input-group">
            <div class="label">十六进制 (16)</div>
            <el-input
              v-model="hexadecimal"
              :class="{ 'active': activeBase === 16 }"
              @focus="setActiveBase(16)"
              @input="handleHexInput"
              placeholder="输入十六进制数"
            >
              <template #prefix>0x</template>
            </el-input>
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="actions">
          <el-button @click="clearAll">清空</el-button>
          <el-button type="primary" @click="copyActive">复制当前值</el-button>
        </div>
      </div>

      <!-- 高级选项 -->
      <div class="advanced-section">
        <el-collapse>
          <el-collapse-item title="高级选项" name="1">
            <div class="options">
              <div class="option-group">
                <span>位数：</span>
                <el-radio-group v-model="bitLength" @change="handleBitLengthChange">
                  <el-radio-button label="8">8位</el-radio-button>
                  <el-radio-button label="16">16位</el-radio-button>
                  <el-radio-button label="32">32位</el-radio-button>
                  <el-radio-button label="64">64位</el-radio-button>
                </el-radio-group>
              </div>

              <div class="option-group">
                <span>有符号：</span>
                <el-switch
                  v-model="signed"
                  @change="handleSignedChange"
                />
              </div>
            </div>

            <!-- 补充信息显示 -->
            <div class="extra-info" v-if="decimal">
              <div class="info-row">
                <span>二进制补码：</span>
                <span>{{ twosComplement }}</span>
              </div>
              <div class="info-row">
                <span>范围：</span>
                <span>{{ valueRange }}</span>
              </div>
            </div>
          </el-collapse-item>
        </el-collapse>
      </div>

      <!-- 说明区域 -->
      <div class="info-section">
        <h3>使用说明</h3>
        <ul>
          <li>支持二进制、八进制、十进制、十六进制互转</li>
          <li>可选择数值的位数和有无符号</li>
          <li>自动显示二进制补码和取值范围</li>
          <li>输入框获得焦点时会高亮显示</li>
          <li>支持一键复制转换结果</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { ElMessage } from 'element-plus'

const binary = ref('')
const octal = ref('')
const decimal = ref('')
const hexadecimal = ref('')
const activeBase = ref(10)
const bitLength = ref(32)
const signed = ref(true)

// 设置当前活动的进制
const setActiveBase = (base) => {
  activeBase.value = base
}

// 处理二进制输入
const handleBinaryInput = (value) => {
  if (!value) {
    clearAll()
    return
  }
  if (!/^[01]*$/.test(value)) {
    binary.value = binary.value.replace(/[^01]/g, '')
    return
  }
  try {
    const dec = parseInt(value, 2)
    updateAllValues(dec)
  } catch (error) {
    ElMessage.error('无效的二进制数')
  }
}

// 处理八进制输入
const handleOctalInput = (value) => {
  if (!value) {
    clearAll()
    return
  }
  if (!/^[0-7]*$/.test(value)) {
    octal.value = octal.value.replace(/[^0-7]/g, '')
    return
  }
  try {
    const dec = parseInt(value, 8)
    updateAllValues(dec)
  } catch (error) {
    ElMessage.error('无效的八进制数')
  }
}

// 处理十进制输入
const handleDecimalInput = (value) => {
  if (!value) {
    clearAll()
    return
  }
  if (!/^-?\d*$/.test(value)) {
    decimal.value = decimal.value.replace(/[^\d-]/g, '')
    return
  }
  try {
    const dec = parseInt(value, 10)
    updateAllValues(dec)
  } catch (error) {
    ElMessage.error('无效的十进制数')
  }
}

// 处理十六进制输入
const handleHexInput = (value) => {
  if (!value) {
    clearAll()
    return
  }
  if (!/^[0-9A-Fa-f]*$/.test(value)) {
    hexadecimal.value = hexadecimal.value.replace(/[^0-9A-Fa-f]/g, '')
    return
  }
  try {
    const dec = parseInt(value, 16)
    updateAllValues(dec)
  } catch (error) {
    ElMessage.error('无效的十六进制数')
  }
}

// 更新所有进制的值
const updateAllValues = (decimalValue) => {
  const maxValue = signed.value ? Math.pow(2, bitLength.value - 1) - 1 : Math.pow(2, bitLength.value) - 1
  const minValue = signed.value ? -Math.pow(2, bitLength.value - 1) : 0

  if (decimalValue > maxValue || decimalValue < minValue) {
    ElMessage.warning(`数值超出范围 (${minValue} 到 ${maxValue})`)
    return
  }

  decimal.value = decimalValue.toString()
  binary.value = decimalValue.toString(2)
  octal.value = decimalValue.toString(8)
  hexadecimal.value = decimalValue.toString(16).toUpperCase()
}

// 清空所有输入
const clearAll = () => {
  binary.value = ''
  octal.value = ''
  decimal.value = ''
  hexadecimal.value = ''
}

// 复制当前活动进制的值
const copyActive = async () => {
  let valueToCopy = ''
  switch (activeBase.value) {
    case 2: valueToCopy = binary.value ? `0b${binary.value}` : ''; break
    case 8: valueToCopy = octal.value ? `0o${octal.value}` : ''; break
    case 10: valueToCopy = decimal.value; break
    case 16: valueToCopy = hexadecimal.value ? `0x${hexadecimal.value}` : ''; break
  }

  if (!valueToCopy) {
    ElMessage.warning('没有可复制的内容')
    return
  }

  try {
    await navigator.clipboard.writeText(valueToCopy)
    ElMessage.success('复制成功')
  } catch (error) {
    ElMessage.error('复制失败')
  }
}

// 处理位数变化
const handleBitLengthChange = () => {
  if (decimal.value) {
    const dec = parseInt(decimal.value, 10)
    updateAllValues(dec)
  }
}

// 处理有符号切换
const handleSignedChange = () => {
  if (decimal.value) {
    const dec = parseInt(decimal.value, 10)
    updateAllValues(dec)
  }
}

// 计算二进制补码
const twosComplement = computed(() => {
  if (!decimal.value) return ''
  const dec = parseInt(decimal.value, 10)
  if (dec >= 0) return binary.value
  
  const bits = bitLength.value
  const positiveValue = Math.pow(2, bits) + dec
  return positiveValue.toString(2).padStart(bits, '0')
})

// 计算取值范围
const valueRange = computed(() => {
  const bits = bitLength.value
  if (signed.value) {
    const min = -Math.pow(2, bits - 1)
    const max = Math.pow(2, bits - 1) - 1
    return `${min} 到 ${max}`
  } else {
    const max = Math.pow(2, bits) - 1
    return `0 到 ${max}`
  }
})
</script>

<style scoped>
.base-converter {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  margin-top: 80px;
}

.converter-container {
  margin-top: 20px;
}

.input-section {
  margin: 20px 0;
}

.number-input {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.input-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.label {
  width: 120px;
  font-weight: bold;
}

.input-group .el-input {
  flex: 1;
}

.input-group .el-input.active :deep(.el-input__inner) {
  border-color: #409EFF;
  box-shadow: 0 0 0 1px #409EFF;
}

.actions {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 20px;
}

.advanced-section {
  margin: 20px 0;
}

.options {
  padding: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

.option-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.extra-info {
  margin-top: 15px;
  padding: 10px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 5px 0;
}

.info-section {
  margin-top: 30px;
  padding: 20px;
  background-color: #f5f7fa;
  border-radius: 8px;
}

.info-section ul {
  padding-left: 20px;
  margin: 10px 0;
}

.info-section li {
  margin: 8px 0;
  line-height: 1.5;
}

:deep(.el-input__inner) {
  font-family: monospace;
  font-size: 14px;
}
</style> 